import { COMMANDS } from "$lib/commands/commands";
import { file, frameIndex, interaction, layerIndex, layers } from "$lib/state";
import { createDialog, notify } from "../../../../ceyper-common";
import { derived } from "svelte/store";
import { notImplemented } from "$lib/commands/common";

const isEditingLayer = derived(file, ($file) => !!$file);
const isLayerCountGreaterThanOne = derived(layers, ($layers) => ($layers?.length ?? 0) > 1);
const isLayerSelectedNotFirst = derived(layerIndex, ($layer) => $layer !== 0);
const isLayerSelectedNotLast = derived(
    [layerIndex, layers],
    ([$layer, $layers]) => !!$layers && $layer !== $layers.length - 1
);

export function registerLayerCommands() {
    const { add, destroy } = COMMANDS.registerActions("all");

    add(
        "edit.add-layer",
        async () => {
            interaction.value!.addLayer(frameIndex.value!, layerIndex.value! + 1);
        },
        isEditingLayer
    );

    add(
        "edit.duplicate-layer",
        async () => {
            interaction.value!.duplicateLayer(frameIndex.value!, layerIndex.value!);
        },
        isEditingLayer
    );

    add(
        "edit.remove-layer",
        async (layer = layerIndex.value!) => {
            if (layers.value!.length <= 1) {
                notify({
                    type: "message",
                    level: "warn",
                    title: "Cannot delete last layer",
                    message: "Cannot delete last layer",
                });
                return;
            }

            interaction.value!.removeLayer(frameIndex.value!, layer);
        },
        isLayerCountGreaterThanOne
    );

    add(
        "edit.move-layer-up",
        async () => {
            const current = layerIndex.value!;
            if (current === layers.value!.length - 1) return;

            interaction.value!.moveLayer(frameIndex.value!, current, current + 1);
        },
        isLayerSelectedNotLast
    );

    add(
        "edit.move-layer-down",
        async () => {
            const current = layerIndex.value!;
            if (current === 0) return;

            interaction.value!.moveLayer(frameIndex.value!, current, current - 1);
        },
        isLayerSelectedNotFirst
    );

    add(
        "edit.move-layer-up-index",
        async (index: number) => {
            if (index === layers.value!.length - 1) return;

            interaction.value!.moveLayer(frameIndex.value!, index, index + 1);
        },
        isEditingLayer
    );

    add(
        "edit.move-layer-down-index",
        async (index: number) => {
            if (index === 0) return;

            interaction.value!.moveLayer(frameIndex.value!, index, index - 1);
        },
        isEditingLayer
    );

    add(
        "edit.merge-layers",
        async () => {
            const current = layerIndex.value!;

            interaction.value!.internal.merge_layers_in_frame(frameIndex.value!, current);

            return;
        },
        isLayerSelectedNotFirst
    );

    add(
        "edit.rename-layer",
        async (layer = layerIndex.value!) => {
            const layerInfo = layers.value![layer];

            const result = await createDialog({
                id: "rename-layer",
                title: "Rename Layer",
                fields: {
                    name: {
                        name: "Name",
                        type: "string",
                        value: layerInfo.name,
                    },
                },
            });

            const name = result?.name;

            if (!name) return;

            interaction.value!.renameLayer(frameIndex.value!, layer, name);
        },
        isEditingLayer
    );

    add(
        "edit.toggle-layer-visibility",
        (layer = layerIndex.value!, value) => {
            let current = layers.value![layer].visible;

            interaction.value!.setLayerVisibility(frameIndex.value!, layer, value ?? !current);
        },
        isEditingLayer
    );

    let x = 1;
    let y = 1;
    add("general.debug", (layer = layerIndex.value!) => {
        // file.value!.internal.apply_layer_offset(layer);
    });

    add(
        "edit.toggle-layer-focus",
        async () => {
            notImplemented();
        },
        false
    );

    add(
        "edit.toggle-view-tiling",
        async () => {
            notImplemented();
        },
        false
    );

    add(
        "edit.select-layer-up",
        async () => {
            interaction.value.setLayer(wrapLayer(layerIndex.value! + 1));
        },
        isEditingLayer
    );

    add(
        "edit.select-layer-down",
        async () => {
            interaction.value.setLayer(wrapLayer(layerIndex.value! - 1));
        },
        isEditingLayer
    );

    function wrapLayer(layer: number) {
        const length = layers.value!.length;
        return (layer + length) % length;
    }

    function clampLayer(layer: number) {
        const length = layers.value!.length;
        return Math.max(0, Math.min(layer, length - 1));
    }

    return { destroy };
}
